/**
 * A régua de presença, do mais quente ao mais frio.
 *
 * <p>Os degraus e as cores são do Theo. O que o código acrescenta é onde cada corte cai:
 * "hoje" e "ontem" são <b>dias de calendário</b>, e não múltiplos de 24 horas. Quem
 * entrou ontem às 23h esteve aqui ontem, mesmo que faça duas horas — contar em janelas
 * de 24h faria o selo mudar de cor no meio da madrugada, sem nada ter acontecido.
 *
 * <p>Os dois estados que piscam são os dois extremos, e é de propósito: piscar chama o
 * olho, e só "está aqui agora" e "nunca entrou" merecem ser vistos sem serem procurados.
 *
 * <p>Mora fora da tela porque a fronteira de dia é exatamente o tipo de conta que erra
 * uma vez por dia, à meia-noite, e ninguém está olhando na hora. Fora do React, ela tem
 * teste.
 */

export type ClassePresenca = 'on' | 'hoje' | 'ontem' | 'semana' | 'sumido' | 'nunca';

export interface Presenca {
  classe: ClassePresenca;
  rotulo: string;
  titulo: string;
  /**
   * Se a ausência, por si só, já justifica o selo de "Inativo" no card — sem ninguém
   * precisar escrever isso no cargo.
   *
   * <p>Vale para quem nunca entrou e para quem passou de {@link INATIVO_APOS_DIAS} dias
   * sem sinal. É um booleano <b>derivado</b> do mesmo `dias`/`vistoEm` que já decide a
   * classe e o rótulo — não uma segunda régua: mudar o corte aqui é mudar um número
   * neste arquivo, e a Equipe nunca precisa recalcular dias por conta própria.
   *
   * <p>O cargo continua sendo só cargo. Isto não o substitui nem o edita — a tela é
   * quem decide o que fazer com o booleano (ver `Equipe.tsx`).
   */
  inativoPorAusencia: boolean;
}

/** Quanto tempo depois do último sinal a pessoa ainda conta como "aqui agora". */
export const ONLINE_ATE_MIN = 10;

/**
 * A partir de quantos dias sem nenhum sinal a ausência sozinha já é motivo para marcar
 * a pessoa como inativa — pedido do Theo, depois de ver o card do Samuel (cargo escrito
 * à mão como "Inativo") e perguntar por que isso não acontecia sozinho para quem
 * simplesmente sumiu.
 */
export const INATIVO_APOS_DIAS = 15;

/** O dia de calendário de uma data, sem a hora. */
function soODia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * @param agora injetável só para teste. Em produção é sempre o relógio da máquina de
 *   quem está olhando — é a presença dele em relação ao agora dele que importa.
 */
export function presencaDe(vistoEm: string | null, agora: Date = new Date()): Presenca {
  if (!vistoEm) {
    return {
      classe: 'nunca',
      rotulo: 'Nunca entrou',
      titulo: 'Esta pessoa ainda não acessou o GG Pilot',
      inativoPorAusencia: true,
    };
  }

  const visto = new Date(vistoEm);
  const minutos = (agora.getTime() - visto.getTime()) / 60000;
  const quando = visto.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (minutos < ONLINE_ATE_MIN) {
    return { classe: 'on', rotulo: 'Online', titulo: `Ativo agora · ${quando}`, inativoPorAusencia: false };
  }

  const dias = Math.round((soODia(agora).getTime() - soODia(visto).getTime()) / 86_400_000);

  if (dias <= 0) {
    return {
      classe: 'hoje',
      rotulo: 'Hoje',
      titulo: `Esteve aqui hoje · ${quando}`,
      inativoPorAusencia: false,
    };
  }
  if (dias === 1) {
    return {
      classe: 'ontem',
      rotulo: 'Ontem',
      titulo: `Esteve aqui ontem · ${quando}`,
      inativoPorAusencia: false,
    };
  }
  if (dias < 7) {
    return {
      classe: 'semana',
      rotulo: `Há ${dias} dias`,
      titulo: `Última vez em ${quando}`,
      inativoPorAusencia: false,
    };
  }
  return {
    classe: 'sumido',
    rotulo: `Há ${dias} dias`,
    titulo: `Última vez em ${quando}`,
    inativoPorAusencia: dias >= INATIVO_APOS_DIAS,
  };
}
